import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, RotateCcw, User, Zap } from 'lucide-react';
import { cn } from '@hts/ui';
import { useGet, apiPost } from '../../hooks/useApi.js';
import { PriorityBadge, SlaChip, ModeBadge } from '../../components/ui/StatusBadge.js';
import { CardSkeleton } from '../../components/ui/Skeleton.js';
import { EmptyState, ErrorState } from '../../components/ui/EmptyState.js';
import { scoreColor, scoreBarColor, relativeTime, formatDate } from '../../lib/format.js';

function fmtTime(iso: string): string {
  // Explicit business timezone so the display (and tests) are runner-locale independent.
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/Chicago' });
}
import type { DispatchDecision, DispatchCandidate } from '../../api/types.js';
import { FillProposalsSection } from './FillProposalsSection.js';

export function DispatchBoardPage() {
  const { data, loading, error, refetch } = useGet<{ items: DispatchDecision[] }>('/api/v1/dispatch');
  const [accepting, setAccepting] = React.useState<Record<string, string | null>>({});
  const [accepted, setAccepted] = React.useState<Record<string, string>>({});
  const [override, setOverride] = React.useState<{ decisionId: string; open: boolean } | null>(null);

  const decisions = data?.items ?? [];
  // Server status is authoritative across refresh/navigation; local state only provides immediate feedback.
  const pending = decisions.filter(d => !accepted[d.id] && (!d.status || d.status === 'pending'));
  const done = decisions.filter(d => !!accepted[d.id] || (!!d.status && d.status !== 'pending'));

  async function accept(decisionId: string, techId: string) {
    setAccepting(prev => ({ ...prev, [decisionId]: techId }));
    try {
      await apiPost(`/api/v1/dispatch/${decisionId}/accept`, { techId });
      setAccepted(prev => ({ ...prev, [decisionId]: techId }));
      refetch();
    } finally {
      setAccepting(prev => ({ ...prev, [decisionId]: null }));
    }
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-[hsl(var(--content))]">Dispatching</h1>
          <p className="text-sm text-[hsl(var(--content-subtle))] mt-0.5">
            AI-recommended technician assignments. Shadow mode shows only; Recommend requires approval; Auto dispatches immediately.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => refetch()} className="rounded-lg border border-[hsl(var(--border))] px-3 py-2 text-sm text-[hsl(var(--content-subtle))] hover:bg-[hsl(var(--surface-subtle))] transition-colors flex items-center gap-1.5">
            <RotateCcw className="h-3.5 w-3.5" /> Refresh
          </button>
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Pending', value: pending.length, color: 'text-amber-600' },
          { label: 'Accepted', value: done.length, color: 'text-green-600' },
          { label: 'Shadow', value: decisions.filter(d => d.mode === 'shadow').length, color: 'text-slate-600' },
          { label: 'Auto', value: decisions.filter(d => d.mode === 'auto').length, color: 'text-purple-600' },
        ].map(stat => (
          <div key={stat.label} className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-4 py-3">
            <p className={`text-2xl font-bold tabular-nums ${stat.color}`}>{stat.value}</p>
            <p className="text-xs text-[hsl(var(--content-subtle))] mt-0.5">{stat.label}</p>
          </div>
        ))}
      </div>

      {error ? (
        <ErrorState message={`Could not load dispatch decisions (${error}).`} onRetry={refetch} />
      ) : loading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={i} lines={5} />)}
        </div>
      ) : pending.length === 0 ? (
        <EmptyState icon="✅" title="All caught up!" description="No pending dispatch decisions. New tickets will appear here as they arrive." />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {pending.map(decision => (
            <DispatchCard
              key={decision.id}
              decision={decision}
              accepting={accepting[decision.id] ?? null}
              onAccept={(techId) => accept(decision.id, techId)}
            />
          ))}
        </div>
      )}

      <FillProposalsSection />

      {done.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-[hsl(var(--content-subtle))] mb-3 uppercase tracking-wide">Recently Accepted</h2>
          <div className="space-y-2">
            {done.map(d => (
              <div key={d.id} className="flex items-center gap-3 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-4 py-3">
                <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                <span className="text-sm text-[hsl(var(--content))] flex-1 truncate"><span className="font-mono mr-1.5">#{d.ticketId}</span>{d.ticketSummary}</span>
                <span className="text-xs text-[hsl(var(--content-subtle))]">→ {d.candidates.find(c => c.techId === (accepted[d.id] ?? d.overriddenTo ?? d.chosenTechId))?.techName ?? accepted[d.id] ?? d.overriddenTo ?? d.chosenTechId}</span>
                <span className="text-xs text-[hsl(var(--content-placeholder))]">{relativeTime(d.createdAt)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ScoreBar({ label, value }: { label: string; value: number }) {
  return (
    <div className="space-y-0.5">
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-[hsl(var(--content-subtle))]">{label}</span>
        <span className="text-[10px] font-mono text-[hsl(var(--content))]">{(value * 100).toFixed(0)}</span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-[hsl(var(--surface-subtle))]">
        <div
          className={`h-full rounded-full transition-all ${scoreBarColor(value)}`}
          style={{ width: `${value * 100}%` }}
        />
      </div>
    </div>
  );
}

function CandidateRow({ candidate, rank, onAccept, accepting, isAccepting }: {
  candidate: DispatchCandidate;
  rank: number;
  onAccept: () => void;
  accepting: boolean;
  isAccepting: boolean;
}) {
  const [expanded, setExpanded] = React.useState(false);
  const rankColors = ['', 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300', 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300', 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'];

  return (
    <div className={cn(
      'rounded-lg border transition-all',
      rank === 1 ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/10' : 'border-[hsl(var(--border))] bg-[hsl(var(--surface-subtle))]'
    )}>
      <div className="flex items-center gap-3 px-3 py-2.5">
        <span className={cn('flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold shrink-0', rankColors[rank])}>
          #{rank}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-[hsl(var(--content))] truncate">{candidate.techName}</span>
            <span className={cn('text-xs font-bold tabular-nums', scoreColor(candidate.score))}>
              {(candidate.score * 100).toFixed(0)}%
            </span>
            {(candidate.skillScore ?? 0) < 0.25 && (
              <span className="rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 text-[10px] font-semibold" title="Low skill match for this ticket">
                low skill match
              </span>
            )}
          </div>
          <p className="text-xs text-[hsl(var(--content-subtle))] truncate">{candidate.reason}</p>
          <p className="text-[11px] mt-0.5">
            {candidate.proposedStart && candidate.proposedEnd ? (
              <span className="text-[hsl(var(--content-subtle))]">
                <span aria-label="proposed start window" className="font-medium text-[hsl(var(--content))]">Proposed: {formatDate(candidate.proposedStart)} · {fmtTime(candidate.proposedStart)}–{fmtTime(candidate.proposedEnd)}</span>
                {candidate.proposedReason ? ' — ' + candidate.proposedReason : ''}
              </span>
            ) : candidate.unavailableReason ? (
              <span className="text-amber-600 dark:text-amber-400" role="note">Unavailable — {candidate.unavailableReason}</span>
            ) : (
              <span className="text-[hsl(var(--content-placeholder))]">No proposed start window (schedule data unavailable)</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            onClick={() => setExpanded(v => !v)}
            className="text-xs text-[hsl(var(--content-subtle))] hover:text-[hsl(var(--content))] px-1.5 py-0.5 rounded border border-[hsl(var(--border))] transition-colors"
          >
            Why?
          </button>
          <button
            onClick={onAccept}
            disabled={accepting}
            className={cn(
              'flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors',
              rank === 1
                ? 'bg-green-600 text-white hover:bg-green-700 disabled:opacity-50'
                : 'border border-[hsl(var(--border))] text-[hsl(var(--content-subtle))] hover:bg-[hsl(var(--surface))] disabled:opacity-50'
            )}
          >
            {isAccepting ? '…' : rank === 1 ? '✓ Accept' : 'Assign'}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="px-3 pb-3 space-y-2">
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            <ScoreBar label="Skill match" value={candidate.skillScore} />
            <ScoreBar label="Availability" value={candidate.availabilityScore} />
            <ScoreBar label="Workload" value={candidate.workloadScore} />
            <ScoreBar label="Familiarity" value={candidate.familiarityScore} />
            <ScoreBar label="Urgency" value={candidate.urgencyScore} />
          </div>
          {candidate.availableAt && (
            <p className="text-[10px] text-[hsl(var(--content-subtle))]">
              Available: {candidate.availableAt <= new Date().toISOString() ? 'Now' : relativeTime(candidate.availableAt).replace(' ago', '')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function DispatchCard({ decision, accepting, onAccept }: {
  decision: DispatchDecision;
  accepting: string | null;
  onAccept: (techId: string) => void;
}) {
  return (
    <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-[hsl(var(--border))] bg-[hsl(var(--surface-subtle))]">
        <div className="flex items-center gap-2 mb-1">
          <ModeBadge mode={decision.mode} />
          <span className="text-xs text-[hsl(var(--content-placeholder))]">{relativeTime(decision.createdAt)}</span>
        </div>
        <p className="text-sm font-semibold text-[hsl(var(--content))] truncate"><span className="font-mono mr-1.5">#{decision.ticketId}</span>{decision.ticketSummary}</p>
        <p className="text-xs text-[hsl(var(--content-subtle))] mt-0.5">Board: {decision.board}</p>
      </div>

      <div className="p-4 space-y-2">
        {decision.candidates.map(c => (
          <CandidateRow
            key={c.techId}
            candidate={c}
            rank={c.rank}
            onAccept={() => onAccept(c.techId)}
            accepting={!!accepting}
            isAccepting={accepting === c.techId}
          />
        ))}
      </div>
    </div>
  );
}
